import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import apiClient, { settingsAPI } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Save, Loader2, Eye, EyeOff, Lock, User, Mail, Phone, Briefcase, Globe, Bell, Shield, Calendar, Camera, Trash2 } from 'lucide-react';
import { ROLE_MAP } from '../lib/utils';
import { toast } from 'sonner';

function Field({ label, icon: Icon, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
        {label}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default function ProfilePage() {
  const { user: authUser, refreshProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    apiClient.get('/auth/profile').then(r => setProfile(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const update = (key, value) => setProfile(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put('/auth/profile', {
        full_name: profile.full_name, phone: profile.phone, position: profile.position,
        timezone: profile.timezone, language: profile.language,
        notify_analysis: profile.notify_analysis, notify_reports: profile.notify_reports, notify_alerts: profile.notify_alerts,
      });
      setProfile(res.data);
      if (refreshProfile) refreshProfile();
      toast.success('Perfil actualizado');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (pwForm.newPw !== pwForm.confirm) { toast.error('Las contraseñas no coinciden'); return; }
    if (pwForm.newPw.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    setSavingPw(true);
    try {
      await settingsAPI.changePassword({ current_password: pwForm.current, new_password: pwForm.newPw });
      toast.success('Contraseña actualizada');
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSavingPw(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Solo se permiten imágenes'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('La imagen no puede superar 2MB'); return; }
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiClient.post('/auth/profile/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProfile(prev => ({ ...prev, avatar_url: res.data.avatar_url }));
      if (refreshProfile) refreshProfile();
      toast.success('Foto de perfil actualizada');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al subir imagen'); }
    setUploadingAvatar(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveAvatar = async () => {
    try {
      await apiClient.delete('/auth/profile/avatar');
      setProfile(prev => ({ ...prev, avatar_url: null }));
      if (refreshProfile) refreshProfile();
      toast.success('Foto eliminada');
    } catch { toast.error('Error'); }
  };

  const p = profile || {};
  const initials = (p.full_name || p.email || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  if (loading) return <div className="px-6 py-6"><div className="animate-pulse space-y-4"><div className="h-8 w-48 bg-muted rounded" /><div className="h-64 bg-muted rounded-xl" /></div></div>;

  return (
    <div className="h-full overflow-y-auto" data-testid="profile-page">
      <div className="px-6 lg:px-8 py-6 max-w-[860px]">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>

          {/* Header card with avatar */}
          <Card className="rounded-xl border-border/50 shadow-sm mb-6 overflow-hidden">
            <div className="h-20" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)' }} />
            <CardContent className="px-6 pb-6 relative">
              {/* Avatar overlapping banner */}
              <div className="absolute -top-14 left-6 group">
                <div className="relative">
                  {p.avatar_url ? (
                    <img src={p.avatar_url} alt={p.full_name} className="w-20 h-20 rounded-2xl object-cover shadow-lg border-4 border-card" data-testid="profile-avatar-img" />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl font-display font-bold text-white shadow-lg border-4 border-card" style={{ background: 'var(--accent-primary)' }} data-testid="profile-avatar-initials">
                      {initials}
                    </div>
                  )}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="absolute inset-0 rounded-2xl flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-all duration-200 cursor-pointer"
                    data-testid="avatar-upload-trigger"
                  >
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {uploadingAvatar ? <Loader2 className="h-5 w-5 text-white animate-spin" /> : <Camera className="h-5 w-5 text-white" />}
                    </span>
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" data-testid="avatar-file-input" />
                  {p.avatar_url && (
                    <button onClick={handleRemoveAvatar} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-card border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Eliminar foto" data-testid="avatar-remove-btn">
                      <Trash2 className="h-2.5 w-2.5 text-muted-foreground" />
                    </button>
                  )}
                </div>
              </div>
              {/* Info below banner */}
              <div className="pt-12 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="min-w-0">
                  <h1 className="font-display text-xl font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{p.full_name || 'Sin nombre'}</h1>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>{p.email}</span>
                    <Badge variant="outline" className="text-[10px] shrink-0" style={{ borderColor: 'var(--accent-primary)', color: 'var(--accent-primary)' }}>{ROLE_MAP[p.role] || p.role}</Badge>
                    {p.position && <span className="text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>· {p.position}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.company_name}</p>
                  {p.created_at && <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Desde {new Date(p.created_at).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal info */}
          <Card className="rounded-xl border-border/50 shadow-sm mb-6">
            <CardContent className="p-6">
              <h3 className="text-xs font-display font-semibold uppercase tracking-wide text-muted-foreground mb-5" style={{ letterSpacing: '0.8px' }}>Información Personal</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                <Field label="Nombre completo" icon={User}>
                  <Input value={p.full_name || ''} onChange={(e) => update('full_name', e.target.value)} />
                </Field>
                <Field label="Correo electrónico" icon={Mail} hint="No editable — contacta al administrador">
                  <Input value={p.email || ''} disabled className="bg-muted/30" />
                </Field>
                <Field label="Cargo / Posición" icon={Briefcase}>
                  <Input value={p.position || ''} onChange={(e) => update('position', e.target.value)} placeholder="Ej: Director de Finanzas" />
                </Field>
                <Field label="Teléfono" icon={Phone}>
                  <Input value={p.phone || ''} onChange={(e) => update('phone', e.target.value)} placeholder="+52 33 1234 5678" />
                </Field>
                <Field label="Zona horaria" icon={Globe}>
                  <Select value={p.timezone || 'America/Mexico_City'} onValueChange={(v) => update('timezone', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/Mexico_City">Ciudad de México (UTC-6)</SelectItem>
                      <SelectItem value="America/Monterrey">Monterrey (UTC-6)</SelectItem>
                      <SelectItem value="America/Tijuana">Tijuana (UTC-8)</SelectItem>
                      <SelectItem value="America/Cancun">Cancún (UTC-5)</SelectItem>
                      <SelectItem value="America/Bogota">Bogotá (UTC-5)</SelectItem>
                      <SelectItem value="America/New_York">Nueva York (UTC-5)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Idioma" icon={Globe}>
                  <Select value={p.language || 'es'} onValueChange={(v) => update('language', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Separator className="my-6" />
              <h3 className="text-xs font-display font-semibold uppercase tracking-wide text-muted-foreground mb-5" style={{ letterSpacing: '0.8px' }}>Notificaciones</h3>
              <div className="space-y-4 max-w-md">
                <div className="flex items-center justify-between">
                  <div><p className="text-sm font-medium">Análisis completados</p><p className="text-xs text-muted-foreground">Notificar cuando un análisis termine de ejecutarse</p></div>
                  <Switch checked={p.notify_analysis !== false} onCheckedChange={(v) => update('notify_analysis', v)} />
                </div>
                <div className="flex items-center justify-between">
                  <div><p className="text-sm font-medium">Reportes generados</p><p className="text-xs text-muted-foreground">Notificar cuando se genere un nuevo reporte</p></div>
                  <Switch checked={p.notify_reports !== false} onCheckedChange={(v) => update('notify_reports', v)} />
                </div>
                <div className="flex items-center justify-between">
                  <div><p className="text-sm font-medium">Alertas de riesgo</p><p className="text-xs text-muted-foreground">Notificar cuando se detecten alertas críticas</p></div>
                  <Switch checked={p.notify_alerts !== false} onCheckedChange={(v) => update('notify_alerts', v)} />
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <Button onClick={handleSave} disabled={saving} style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Guardar cambios
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Security */}
          <Card className="rounded-xl border-border/50 shadow-sm">
            <CardContent className="p-6">
              <h3 className="text-xs font-display font-semibold uppercase tracking-wide text-muted-foreground mb-5" style={{ letterSpacing: '0.8px' }}>Seguridad</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h4 className="text-sm font-medium flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-muted-foreground" /> Cambiar contraseña</h4>
                  <Field label="Contraseña actual">
                    <div className="relative">
                      <Input type={showPw ? 'text' : 'password'} value={pwForm.current} onChange={(e) => setPwForm({...pwForm, current: e.target.value})} />
                      <button className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPw(!showPw)}>{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    </div>
                  </Field>
                  <Field label="Nueva contraseña" hint="Mínimo 6 caracteres">
                    <Input type="password" value={pwForm.newPw} onChange={(e) => setPwForm({...pwForm, newPw: e.target.value})} />
                  </Field>
                  <Field label="Confirmar contraseña">
                    <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({...pwForm, confirm: e.target.value})} />
                  </Field>
                  <Button onClick={handleChangePassword} disabled={savingPw || !pwForm.current || !pwForm.newPw} variant="outline">
                    {savingPw ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Lock className="h-4 w-4 mr-1.5" />} Cambiar contraseña
                  </Button>
                </div>
                <div className="space-y-4">
                  <h4 className="text-sm font-medium flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-muted-foreground" /> Sesión actual</h4>
                  <div className="p-4 rounded-xl bg-muted/20 border border-border/50 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent-subtle)' }}>
                        <Shield className="h-5 w-5" style={{ color: 'var(--accent-primary)' }} />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{p.full_name}</p>
                        <p className="text-xs text-muted-foreground">{p.email}</p>
                      </div>
                      <Badge className="ml-auto text-[10px]" style={{ background: 'rgba(5,150,105,0.1)', color: '#059669', border: '1px solid rgba(5,150,105,0.2)' }}>Activa</Badge>
                    </div>
                    <Separator />
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-muted-foreground">Rol:</span> <span className="font-medium">{ROLE_MAP[p.role] || p.role}</span></div>
                      <div><span className="text-muted-foreground">Empresa:</span> <span className="font-medium">{p.company_name}</span></div>
                      {p.last_login_at && <div className="col-span-2"><span className="text-muted-foreground">Último acceso:</span> <span className="font-medium tabular-nums">{new Date(p.last_login_at).toLocaleString('es-MX')}</span></div>}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

        </motion.div>
      </div>
    </div>
  );
}
